import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {
  DataObjectId,
  ExtensionInfo,
  ExtensionScript,
  GenResp,
  InstalledExtension
} from 'yougile-model'
import {extensionsStore} from '@components/extensions-gallery/extensions-store'
import {Backend} from '../../common/backend'
import {Api} from '../api/api'
import {globals} from '../extensions/globals-base'
import {Connector} from './connector'
import {Sync} from './sync'

type ScriptsResponse = {[id: string]: ExtensionScript[]}

export class ExtensionClient {
  private static async authorizedPostRequest<T, U = unknown>(
    path: string,
    data: U
  ): Promise<T & GenResp> {
    if (!globals.allowExtensions) {
      return {result: 'not_allowed'} as unknown as T & GenResp
    }

    return Backend.postJSON<T, U>(path, {
      ...Backend.loadCred(),
      ...data
    } as U)
  }

  public static onConnected(): void {
    Connector.onRequest('extensions-up', async req => {
      logger.log('RECV extensions-up', req.companies)
      const box = Sync.getCurrentSandbox()
      await Api.reloadAllScriptsInCompany(box)
      extensionsStore.updateModified()
    })
  }

  public static async list(companyId: DataObjectId): Promise<ExtensionInfo[]> {
    const resp = await this.authorizedPostRequest<
      GenResp & {extensions: ExtensionInfo[]}
    >('/data/extension/list', {
      companyId,
      lang: Lang.getLang()
    })

    return resp.extensions ?? []
  }

  public static async listInstalled(
    companyId: DataObjectId
  ): Promise<InstalledExtension[]> {
    const resp = await this.authorizedPostRequest<
      GenResp & {extensions: InstalledExtension[]}
    >('/data/extension/list-installed', {
      companyId
    })

    return resp.extensions ?? []
  }

  public static async install(
    companyId: DataObjectId,
    extensionId: string
  ): Promise<boolean> {
    const resp = await this.authorizedPostRequest('/data/extension/install', {
      companyId,
      extensionId
    })

    return resp?.result === 'ok'
  }

  public static async uninstall(
    companyId: DataObjectId,
    extensionId: string
  ): Promise<boolean> {
    const resp = await this.authorizedPostRequest('/data/extension/uninstall', {
      companyId,
      extensionId
    })
    return resp?.result === 'ok'
  }

  public static async rate(
    companyId: DataObjectId,
    extensionId: string,
    rating: number
  ): Promise<boolean> {
    const resp = await this.authorizedPostRequest('/data/extension/rate', {
      companyId,
      extensionId,
      rating
    })

    return resp?.result === 'ok'
  }

  public static async getScripts(
    companyId: DataObjectId
  ): Promise<ScriptsResponse> {
    try {
      const resp = await this.authorizedPostRequest<
        GenResp & {scripts: ScriptsResponse}
      >('/data/extension/get-scripts', {
        companyId,
        desktopVersion: globals.desktopVersion
      })

      return resp?.scripts ?? {}
    } catch (error) {
      logger.error('[ExtensionClient]: getScripts failed with', error)
      return {}
    }
  }
}
